import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ActivityMetricInfoIcon } from "../../../components/ActivityDetailMetricIcons";
import campaignIcon from "../../../static/assets/campaign.svg";
import movingBlackIcon from "../../../static/assets/moving_black.svg";

const SEVERITY_ORDER = { high: 0, medium: 1, watch: 2 };

const SEVERITY_STYLES = {
  high: { labelKey: "High impact", className: "bg-[#FFE2E5] text-[#B42318]" },
  medium: {
    labelKey: "Medium impact",
    className: "bg-[#FFF4DE] text-[#8A5A00]",
  },
  watch: { labelKey: "To monitor", className: "bg-[#F2F4F7] text-[#475467]" },
};

/** Three equal cards + two gaps (gap-3 = 0.75rem). */
const CARD_WIDTH_CLASS = "w-[calc((100%-1.5rem)/3)] min-w-[220px]";

const RecommendationCard = ({ recommendation, t }) => {
  const severity = recommendation.severity || "watch";
  const style = SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.watch;

  return (
    <div
      className={`${CARD_WIDTH_CLASS} shrink-0 flex flex-col gap-3 border border-[#E5E5E5] rounded-lg p-4`}
    >
      <span
        className={`self-start px-3 py-1 rounded-full text-sm font-semibold ${style.className}`}
      >
        {t(style.labelKey)}
      </span>

      <div className="flex flex-col gap-1">
        <div className="flex flex-row items-center gap-2">
          <img src={campaignIcon} alt="" width={14} height={11} />
          <span className="font-semibold text-sm text-[#000000]">
            {t("Recommendation")}
          </span>
        </div>
        <span className="text-sm text-gray-700">
          {t(recommendation.titleKey, recommendation.titleParams)}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex flex-row items-center gap-2">
          <img src={movingBlackIcon} alt="" width={14} height={8} />
          <span className="font-semibold text-sm text-[#000000]">
            {t("Impact")}
          </span>
        </div>
        <span className="text-sm text-gray-700">
          {t(recommendation.impactKey, recommendation.impactParams)}
        </span>
      </div>
    </div>
  );
};

/**
 * Recommendations block for activity detail Synthèse — placed under KPI vignettes.
 * One vignette per recommendation (severity badge repeated), 3 visible, horizontal scroll beyond.
 */
export const ActivityDetailRecommendationsSection = ({
  recommendations = [],
}) => {
  const { t } = useTranslation();
  const ordered = useMemo(
    () =>
      [...recommendations].sort(
        (first, second) =>
          (SEVERITY_ORDER[first.severity] ?? 99) -
          (SEVERITY_ORDER[second.severity] ?? 99),
      ),
    [recommendations],
  );

  return (
    <article className="bg-white border border-gray-300 rounded-xl p-4 flex flex-col gap-3 w-full min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="m-0 font-bold text-base text-[#000000]">
              {t("Recommendations")}
            </h3>
            <Link
              to="/activity/recommendations-guide"
              className="inline-flex shrink-0 items-center justify-center rounded-full p-0.5 text-[#5E5E5E] hover:bg-[#F3F4F6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A7A6]"
              aria-label={t("How recommendations are built")}
              title={t("How recommendations are built")}
            >
              <ActivityMetricInfoIcon />
            </Link>
          </div>
          <span className="text-gray-500 text-sm">
            {t("Prioritized actions to optimize your filling")}
          </span>
        </div>
      </div>

      {ordered.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center text-[#00A7A6] py-6 gap-2">
          <span className="fas fa-check-circle text-xl" aria-hidden />
          <span className="text-sm">
            {t("No recommendation for this collection")}
          </span>
        </div>
      ) : (
        <div className="flex flex-row gap-3 overflow-x-auto pb-1">
          {ordered.map((recommendation) => (
            <RecommendationCard
              key={recommendation.id || recommendation.key}
              recommendation={recommendation}
              t={t}
            />
          ))}
        </div>
      )}
    </article>
  );
};
